import React, { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import BlockIcon from "@mui/icons-material/Block";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import Swal from "sweetalert2";

import LocationSelector from "../components/LocationSelector";
import formatCurrency from "../shared/formatCurrency";
import { useAuth } from "../contexts/AuthContext";
import {
  getAvailableDestinationNzengos,
  getNzengoCurrentContext,
  submitNzengoTransferRequest,
} from "../services/nzengoTransferService";

const brand = "#800b33";
const brandHover = "#6c0a2b";

const currency = (amount) => `TZS ${formatCurrency(amount, "en-TZ")}`;

export default function NzengoTransferRequest() {
  const { user } = useAuth();
  const [scenario, setScenario] = useState("eligible");
  const [region, setRegion] = useState("");
  const [district, setDistrict] = useState("");
  const [selectedGroupId, setSelectedGroupId] = useState("");
  const [submittedTransfer, setSubmittedTransfer] = useState(null);

  const context = useMemo(() => getNzengoCurrentContext(user, scenario), [user, scenario]);
  const isEligible = Number(context.outstandingAmount) === 0;
  const destinationGroups = useMemo(
    () =>
      getAvailableDestinationNzengos({
        region,
        district,
        currentGroupId: context.currentGroup?.id,
      }),
    [context.currentGroup?.id, district, region]
  );
  const selectedGroup = destinationGroups.find((group) => group.id === selectedGroupId);

  const handleSubmit = () => {
    try {
      const transfer = submitNzengoTransferRequest({
        memberId: context.member.id,
        sourceGroupId: context.currentGroup.id,
        destinationGroupId: selectedGroupId,
        outstandingAmount: context.outstandingAmount,
      });
      setSubmittedTransfer(transfer);
      Swal.fire({
        icon: "success",
        title: "Transfer Request Submitted",
        text: "Your request is now pending destination chairperson approval.",
        confirmButtonColor: brand,
      });
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Unable to submit transfer",
        text: error.message,
        confirmButtonColor: brand,
      });
    }
  };

  if (submittedTransfer) {
    return (
      <Box sx={{ p: { xs: 2, md: 4 } }}>
        <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
          <CardContent>
            <Stack spacing={2}>
              <Chip color="warning" label="PENDING APPROVAL" sx={{ alignSelf: "flex-start", fontWeight: 800 }} />
              <Typography variant="h5" sx={{ fontWeight: 900, color: brand }}>
                Nzengo Transfer Request
              </Typography>
              <Typography color="text.secondary">
                Your transfer from <b>{submittedTransfer.sourceGroup?.name}</b> to{" "}
                <b>{submittedTransfer.destinationGroup?.name}</b> has been submitted for destination chairperson approval.
              </Typography>
              <Alert severity="info">
                Membership will move only after approval. Your current Nzengo remains active while the request is pending.
              </Alert>
            </Stack>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 4 } }}>
      <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" gap={2} sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 900, color: brand }}>
            Request Nzengo Transfer
          </Typography>
          <Typography variant="subtitle2" color="text.secondary">
            Review eligibility, choose destination Nzengo, then submit for chairperson approval.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button
            variant={scenario === "eligible" ? "contained" : "outlined"}
            onClick={() => {
              setScenario("eligible");
              setSelectedGroupId("");
            }}
            sx={{ bgcolor: scenario === "eligible" ? brand : undefined, "&:hover": { bgcolor: scenario === "eligible" ? brandHover : undefined } }}
          >
            Eligible Demo
          </Button>
          <Button
            variant={scenario === "blocked" ? "contained" : "outlined"}
            onClick={() => {
              setScenario("blocked");
              setSelectedGroupId("");
            }}
            color="warning"
          >
            Debt Demo
          </Button>
        </Stack>
      </Stack>

      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <Card sx={{ borderRadius: 3, boxShadow: 2, height: "100%" }}>
            <CardContent>
              <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 2 }}>
                Eligibility Summary
              </Typography>
              <Stack spacing={1.5}>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Current Nzengo
                  </Typography>
                  <Typography sx={{ fontWeight: 800 }}>{context.currentGroup?.name}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Member Name
                  </Typography>
                  <Typography sx={{ fontWeight: 800 }}>{context.member?.fullName}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Outstanding Obligations
                  </Typography>
                  <Typography sx={{ fontWeight: 900, color: isEligible ? "success.main" : "error.main" }}>
                    {currency(context.outstandingAmount)}
                  </Typography>
                </Box>
                <Chip
                  icon={isEligible ? <CheckCircleIcon /> : <BlockIcon />}
                  label={isEligible ? "Eligible for transfer" : "Transfer blocked until obligations are cleared"}
                  color={isEligible ? "success" : "error"}
                  sx={{ justifyContent: "flex-start", fontWeight: 800 }}
                />
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={8}>
          <Card sx={{ borderRadius: 3, boxShadow: 2 }}>
            <CardContent>
              <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 2 }}>
                Destination Nzengo
              </Typography>
              {!isEligible && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  Clear all outstanding obligations before selecting a destination Nzengo.
                </Alert>
              )}
              <LocationSelector
                region={region}
                district={district}
                required={false}
                disabled={!isEligible}
                onChange={({ region: nextRegion, district: nextDistrict }) => {
                  setRegion(nextRegion);
                  setDistrict(nextDistrict);
                  setSelectedGroupId("");
                }}
                errors={{ region: isEligible && !region ? "Region is required for transfer discovery." : "" }}
              />
              <Divider sx={{ my: 2 }} />
              <Grid container spacing={1.5}>
                {!region && (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">
                      Select a region to view available Nzengo groups.
                    </Typography>
                  </Grid>
                )}
                {region && destinationGroups.length === 0 && (
                  <Grid item xs={12}>
                    <Alert severity="info">No active destination Nzengo matched this location.</Alert>
                  </Grid>
                )}
                {destinationGroups.map((group) => (
                  <Grid item xs={12} md={6} key={group.id}>
                    <Card
                      variant={selectedGroupId === group.id ? "elevation" : "outlined"}
                      sx={{
                        borderRadius: 2,
                        borderColor: selectedGroupId === group.id ? brand : undefined,
                        bgcolor: selectedGroupId === group.id ? "rgba(128, 11, 51, 0.04)" : "background.paper",
                      }}
                    >
                      <CardContent>
                        <Stack spacing={1}>
                          <Typography sx={{ fontWeight: 900 }}>{group.name}</Typography>
                          <Stack direction="row" spacing={0.5} alignItems="center">
                            <LocationOnIcon fontSize="small" color="action" />
                            <Typography variant="body2" color="text.secondary">
                              {group.district}, {group.region}
                            </Typography>
                          </Stack>
                          <Button
                            variant={selectedGroupId === group.id ? "contained" : "outlined"}
                            size="small"
                            disabled={!isEligible}
                            onClick={() => setSelectedGroupId(group.id)}
                            sx={{
                              alignSelf: "flex-start",
                              bgcolor: selectedGroupId === group.id ? brand : undefined,
                              "&:hover": { bgcolor: selectedGroupId === group.id ? brandHover : undefined },
                            }}
                          >
                            {selectedGroupId === group.id ? "Selected" : "Select"}
                          </Button>
                        </Stack>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card sx={{ borderRadius: 3, boxShadow: 2 }}>
            <CardContent>
              <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 2 }}>
                Review
              </Typography>
              <Grid container spacing={1.5}>
                {[
                  ["Member", context.member?.fullName],
                  ["Current Nzengo", context.currentGroup?.name],
                  ["Destination Nzengo", selectedGroup?.name || "—"],
                  ["Region", selectedGroup?.region || region || "—"],
                  ["District", selectedGroup?.district || district || "—"],
                  ["Outstanding Balance", currency(context.outstandingAmount)],
                  ["Eligibility", isEligible ? "Eligible" : "Blocked"],
                ].map(([label, value]) => (
                  <Grid item xs={12} md={3} key={label}>
                    <Typography variant="caption" color="text.secondary">
                      {label}
                    </Typography>
                    <Typography sx={{ fontWeight: 800 }}>{value}</Typography>
                  </Grid>
                ))}
              </Grid>
              <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
                <Button
                  variant="contained"
                  endIcon={<ArrowForwardIcon />}
                  disabled={!isEligible || !selectedGroupId}
                  onClick={handleSubmit}
                  sx={{ bgcolor: brand, "&:hover": { bgcolor: brandHover } }}
                >
                  Submit Transfer Request
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}
